import { Button, Divider, Form, Input, Modal, Popover, Select, Space, TableProps, Tabs, Tag, Tooltip } from 'antd'
import { useCallback, useEffect, useRef, useState } from 'react'
import { io, Socket } from 'socket.io-client'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { authSelector } from '../../redux/slices/authSlice';
import { AUTH_TOKEN_KEY } from '../../config/constants'
import { ZALO_ACCOUNT, PERMISSIONS } from '../../assets/constants/route_path'
import {
  apiCheckSessionActive,
  apiCompleteZaloLogin,
  apiGetListZaloAccountSessions,
  apiGetListZaloAccounts,
  apiGetSessionCredentials,
  apiLogVerifySession,
  apiUpdateListZaloAccounts,
  apiDeleteListZaloAccounts,
  apiUpdateListZaloAccountSessions,
  apiGetListUsers,
} from '../../api'
import { DeleteOutlined, EditOutlined, EyeOutlined, KeyOutlined, LeftCircleOutlined, LoginOutlined, SearchOutlined, UndoOutlined } from '@ant-design/icons'

import { toastError, toastSuccess } from '../../utils/toast'
import CustomTable from '../../components/table'
import defaultParams from '../../assets/constants/default_params'
import { useLoading } from '../../context/LoadingContext'
import FriendsModal from './FriendsModal'
import CompanyMappingModal from './CompanyMappingModal'
import { apiGetListZaloAccountCompanies, apiGetListCompanies } from '../../api'

const TAG_STYLE = { width: 100, textAlign: 'center' as const, marginRight: 0 }

const renderType = (type: string) => {
  if (type === 'ACTIVE') return <Tag style={TAG_STYLE} color="green">Hoạt động</Tag>
  if (type === 'INACTIVE') return <Tag style={TAG_STYLE} color="red">Không hoạt động</Tag>
  if (type === 'CLOSED') return <Tag style={TAG_STYLE} color="orange">Đã đóng</Tag>
  if (type === 'PERSONAL') return <Tag style={TAG_STYLE} color="gray">PERSONAL</Tag>
  if (type === 'OA') return <Tag style={TAG_STYLE} color="blue">OA</Tag>
  if (type === 'QR') return <Tag style={TAG_STYLE} color="geekblue">QR</Tag> 
  return
}

function toArray(value: any): any[] {
  return Array.isArray(value) ? value : []
}

function toIntNumber(value: any): number | null {
  const num = Number(value)
  if (!Number.isFinite(num)) {
    return null
  }
  return num
}

function toIntNumberArray(value: any): number[] {
  if (value == null || value === '') {
    return []
  }
  const values = Array.isArray(value) ? value : String(value).split(',')
  return values
    .map(item => toIntNumber(String(item).trim()))
    .filter((item): item is number => item !== null)
}

function isActiveStatus(status: any): boolean {
  return String(status || "").toUpperCase() === "ACTIVE";
}

export default function Accounts() {
  const [qr, setQr] = useState<string | null>(null)
  const [status, setStatus] = useState<string>('Đang chờ tạo QR...')
  const [modalLogin, setModalLogin] = useState<boolean>(false)
  const socketRef = useRef<Socket | null>(null)
  const [currentPage, setCurrentPage] = useState<number>(defaultParams.PAGE)
  const [pageSize, setPageSize] = useState<number>(defaultParams.LIMIT)
  const { setIsLoading } = useLoading()
  const [tabTransaction, setTabTransaction] = useState<any>({})
  const [zaloAccounts, setZaloAccounts] = useState<any[]>([])
  const [zaloAccountSessions, setZaloAccountSessions] = useState<any[]>([])
  const [user, setUser] = useState<any>([])
  const authState = useSelector(authSelector)
  const navigate = useNavigate()
  const crmUserId = String(authState?.account?.id || '').trim()
  const crmUserIdNumber = toIntNumber(crmUserId)
  const [modelUpdateAccount, setModelUpdateAccount] = useState<any>({ isOpen: false, data: null })
  const [updateAccount, setUpdateAccount] = useState<any>(null)
  const [modelDeleteAccount, setModelDeleteAccount] = useState<any>({ isOpen: false, data: null })
  const [friendsModal, setFriendsModal] = useState<{ open: boolean; account: any }>({ open: false, account: null })
  const [companyModal, setCompanyModal] = useState<{ open: boolean; account: any }>({ open: false, account: null })
  const [companyMap, setCompanyMap] = useState<Map<number, string[]>>(new Map())
  const [companiesOptions, setCompaniesOptions] = useState<Array<{ code: string; name: string }>>([])
  const [cpCodesFilter, setCpCodesFilter] = useState<string[]>([])
  const [currentSessionContext, setCurrentSessionContext] = useState<{ accountId: number | null; sessionId: any | null }>({
    accountId: null,
    sessionId: null,
  })
  const [search, setSearch] = useState<any>({})
  const [formSearch] = Form.useForm()
  const [accountActiveMap, setAccountActiveMap] = useState<Map<number, boolean | null>>(new Map())
  const [verifyingIds, setVerifyingIds] = useState<Set<number>>(new Set())


  const getCrmUserInfo = useCallback((userId: number) => {
    if (!user || user.length === 0) {
      return null
    }
    const userInfo = user.find((u: any) => Number(u?.id) === Number(userId))
    return userInfo || null
  }, [user])

  const formatDate = (isoString: any) => {
    if (!isoString) return '—'
    const date = new Date(isoString)
    // Guard zero-value Go time (0001-01-01) and invalid input.
    if (!Number.isFinite(date.getTime()) || date.getUTCFullYear() < 2000) return '—'
    return date.toLocaleString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
  }

  const formatPhone = (phone: any) => {
    if (!phone) return '';

    return phone.replace(/^\+84/, '0');
  };

  const zaloAccountColumns: TableProps<any>['columns'] = [
    {
      key: 'displayName',
      title: 'Tên hiển thị',
      render: (_, record: any) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record?.displayName || '(no name)'}</div>
          <div style={{ fontSize: 12, color: '#999' }}>#{record?.id}</div>
        </div>
      ),
    },
    {
      key: 'phone',
      title: 'SĐT',
      dataIndex: 'phone',
      render: value => formatPhone(value) || '—',
    },
    {
      key: 'zaloUid',
      title: 'UID',
      dataIndex: 'zaloUid',
      render: (value: any) => {
        if (!value) return '—'
        const s = String(value)
        const tail = s.length > 8 ? `…${s.slice(-6)}` : s
        return (
          <Tooltip title={s}>
            <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{tail}</span>
          </Tooltip>
        )
      },
    },
    {
      key: 'accountType',
      title: 'Loại',
      dataIndex: 'accountType',
      render: value => renderType(value),
    },
    {
      key: 'status',
      title: 'Trạng thái',
      dataIndex: 'status',
      render: value => renderType(value),
    },
    {
      key: 'userName',
      title: 'User phụ trách',
      dataIndex: 'userName',
      render: (_, record) => {
        const activeSessions = toArray(zaloAccountSessions).filter((session: any) => {
          return (
            Number(session?.zaloAccountId) === Number(record?.id) &&
            isActiveStatus(session?.status)
          )
        })

        if (activeSessions.length === 0) {
          return 'Không có'
        }

        const latestSession = activeSessions.reduce((latest: any, current: any) => {
          const latestTime = new Date(latest?.lastActiveAt || latest?.startedAt || 0).getTime()
          const currentTime = new Date(current?.lastActiveAt || current?.startedAt || 0).getTime()
          return currentTime > latestTime ? current : latest
        })
        const userInfo = getCrmUserInfo(Number(latestSession?.userId))
        return userInfo ? userInfo.full_name : 'Không có'
      }
    },
    {
      key: 'sessionStatus',
      title: 'Phiên',
      render: (_, record) => {
        if (!authState?.account?.canCheckSessionActive) {
          return <Tag>—</Tag>
        }
        const accountId = Number(record?.id)
        const accountActive = accountActiveMap.get(accountId)
        const isVerifying = verifyingIds.has(accountId)
        const canVerifyReal = authState?.account?.canGetSessionCredentials && authState?.account?.canLogVerifySession

        // Phase 1c shared-credentials: any user with ACTIVE permission can use
        // the account's active session (BFF gates by permission level, not by
        // session owner). UI mirrors that — if the account has an active
        // session anywhere, render Verify; otherwise prompt this user to QR.
        const loginButton = (
          <Button
            size="small"
            icon={<LoginOutlined />}
            onClick={() => {
              setModalLogin(true)
              setStatus('Đang kết nối tới máy chủ...')
              setQr(null)
              connectSocketAndAuthenticateByUserId()
            }}
          >
            Đăng nhập Zalo
          </Button>
        )

        if (accountActive === null || accountActive === undefined) {
          return <Tag>{isVerifying ? 'Đang verify...' : 'Đang kiểm tra...'}</Tag>
        }
        if (accountActive) {
          return (
            <Space size="small">
              <Tag color="green">Đang hoạt động</Tag>
              {canVerifyReal && (
                <Button size="small" loading={isVerifying} onClick={() => handleVerifyAccount(record)}>
                  Verify thật
                </Button>
              )}
            </Space>
          )
        }
        return (
          <Space size="small">
            <Tag color="red">Chưa có phiên</Tag>
            {loginButton}
          </Space>
        )
      },
    },
    {
      key: 'lastUsedAt',
      title: 'Sử dụng gần nhất',
      dataIndex: 'lastUsedAt',
      render: value => formatDate(value),
    },
    {
      key: 'companies',
      title: 'Đối tác',
      render: (_, record: any) => {
        if (!authState?.account?.canGetListZaloAccountCompanies) {
          return <Tag>—</Tag>
        }
        const cpCodes = companyMap.get(Number(record?.id)) || []
        const visible = cpCodes.slice(0, 2)
        const overflow = cpCodes.slice(2)
        const canEdit = !!authState?.account?.canAddListZaloAccountCompanies
        return (
          <Space size={4}>
            {cpCodes.length === 0 ? (
              <span style={{ color: '#999' }}>—</span>
            ) : (
              visible.map((c) => <Tag key={c} color="blue" style={{ marginRight: 0 }}>{c}</Tag>)
            )}
            {overflow.length > 0 && (
              <Popover
                trigger="click"
                title={`${overflow.length} đối tác khác`}
                content={
                  <Space size={4} wrap style={{ maxWidth: 320 }}>
                    {overflow.map((c) => <Tag key={c} color="blue">{c}</Tag>)}
                  </Space>
                }
              >
                <Tag style={{ cursor: 'pointer' }}>+{overflow.length}</Tag>
              </Popover>
            )}
            {canEdit && (
              <Button size="small" type="link" onClick={() => setCompanyModal({ open: true, account: record })}>
                Sửa
              </Button>
            )}
          </Space>
        )
      },
    },
    {
      key: 'friends',
      title: 'Bạn bè',
      render: (_, record: any) => {
        if (!authState?.account?.canGetListZaloAccountFriends) {
          return <Tag>—</Tag>
        }
        const count = Number(record?.friendsCount || 0)
        const lastSyncedAt = formatDate(record?.friendsLastSyncedAt)
        return (
          <Space size="small" direction="vertical" style={{ alignItems: 'flex-start' }}>
            <a onClick={() => setFriendsModal({ open: true, account: record })}>
              👥 {count} bạn
            </a>
            <span style={{ fontSize: 11, color: '#999' }}>{lastSyncedAt === '—' ? 'Chưa đồng bộ' : `Sync ${lastSyncedAt}`}</span>
          </Space>
        )
      },
    },
    {
      key: 'action',
      title: 'Thao tác',
      render: data => renderAction(data),
    },
  ]

  const zaloAccountSessionColumns: TableProps<any>['columns'] = [
    {
      key: 'sessionCode',
      title: 'Mã phiên',
      dataIndex: 'sessionCode',
    },
    {
      key: 'user',
      title: 'Nhân sự',
      dataIndex: 'user',
      render: (_, record) => {
        const userInfo = getCrmUserInfo(Number(record.userId))
        return userInfo ? userInfo.full_name : 'N/A'
      }
    },
    { 
      key: 'startMethod',
      title: 'Phương thức',
      dataIndex: 'startMethod',
      render: value => renderType(value),
    },
    {
      key: 'status',
      title: 'Trạng thái',
      dataIndex: 'status',
      render: value => renderType(value),
    },
    {
      key: 'startedAt',
      title: 'Bắt đầu',
      dataIndex: 'startedAt',
      render: value => value ? formatDate(value) : 'N/A',
    },
    {
      key: 'lastActiveAt',
      title: 'Hoạt động cuối',
      dataIndex: 'lastActiveAt',
      render: value => value ? formatDate(value) : 'N/A',
    },
    {
      key: 'ipAddress',
      title: 'IP',
      dataIndex: 'ipAddress',
    },
    {
      key: 'userAgent',
      title: 'Thiết bị',
      dataIndex: 'userAgent',
    },
  ]

  const fetchUser = useCallback(async () => {
    try {
      setIsLoading(true)
      const resp = await apiGetListUsers({
        limit: defaultParams.LIMIT,
      })
      if (resp.status === 200) {
        setUser(resp.data?.users || [])
      }
    } catch (error: any) {
      console.log(error)
      toastError('Can not get user information!')
    } finally {
      setIsLoading(false)
    }
    }, [setIsLoading])

  const fetchZaloAccountSessions = useCallback(async (account: any) => {
    const accountList = toArray(account)
    const sessions = [] as any[]
    accountList.forEach((item: any) => {
      if (item.id && !sessions.includes(item.id)) {
        sessions.push(item.id)
      }
    })
    if (sessions.length === 0) {
      setZaloAccountSessions([])
      return
    }

    const resp = await apiGetListZaloAccountSessions({
      page: 1,
      size: Math.max(pageSize, sessions.length * pageSize),
      zaloAccountIds: sessions,
      orderBy: '"createdAt" desc',
    })
    if (resp?.status !== 200) {
      setZaloAccountSessions([])
      return
    }
    setZaloAccountSessions(toArray(resp?.data?.zaloAccountSessions))
  }, [pageSize])

  const handleVerifyAccount = useCallback(async (account: any) => {
    const accountId = Number(account?.id)
    if (!Number.isFinite(accountId) || accountId <= 0) return
    // Phase 1c shared-credentials: pick the latest ACTIVE session on the
    // account regardless of which user owns it. BFF gates the credentials
    // read by the caller's permission, not by session ownership.
    const activeSessions = (zaloAccountSessions || []).filter(
      (s: any) => Number(s?.zaloAccountId) === accountId && isActiveStatus(s?.status)
    )
    if (activeSessions.length === 0) {
      toastError('Tài khoản chưa có phiên hoạt động. Hãy đăng nhập Zalo trước.')
      return
    }
    const latest = activeSessions.reduce((a: any, b: any) => {
      const at = new Date(a?.lastActiveAt || a?.startedAt || 0).getTime()
      const bt = new Date(b?.lastActiveAt || b?.startedAt || 0).getTime()
      return bt > at ? b : a
    })
    const sessionId = latest?.id
    if (!sessionId) {
      toastError('Không tìm thấy session id.')
      return
    }

    setVerifyingIds((prev) => {
      const next = new Set(prev)
      next.add(accountId)
      return next
    })

    let verifySocket: Socket | null = null
    try {
      const credsResp: any = await apiGetSessionCredentials({ sessionId })
      const credentials = credsResp?.data?.credentials
      if (!credentials) {
        throw new Error('Không lấy được credentials từ server.')
      }
      const authToken = localStorage.getItem(AUTH_TOKEN_KEY) || ''
      if (!authToken) {
        throw new Error('Phiên CRM đã hết hạn. Vui lòng đăng nhập lại.')
      }
      const zaloSocketUrl = process.env.REACT_APP_ZALO_SOCKET_URL || 'https://koiosops.com'
      verifySocket = io(zaloSocketUrl, {
        transports: ['websocket'],
        path: process.env.REACT_APP_ZALO_SOCKET_PATH || '/zalo-login/socket.io',
        auth: { token: authToken },
      })

      const result: any = await new Promise((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error('verify timeout')), 35_000)
        verifySocket!.on('connect_error', (err) => {
          clearTimeout(timer)
          reject(err)
        })
        verifySocket!.on('connect', () => {
          verifySocket!
            .timeout(30_000)
            .emitWithAck('verify-session', { credentials })
            .then((res) => {
              clearTimeout(timer)
              resolve(res)
            })
            .catch((err) => {
              clearTimeout(timer)
              reject(err)
            })
        })
      })

      const isActive = !!result?.isActive
      const errorMessage = String(result?.error || '')
      await apiLogVerifySession({ sessionId, isActive, errorMessage })
      setAccountActiveMap((prev) => new Map(prev).set(accountId, isActive))
      if (isActive) {
        toastSuccess('Phiên còn hoạt động.')
      } else {
        toastError(`Phiên đã hết hạn (${errorMessage || 'unknown'}). Hãy đăng nhập lại.`)
      }
    } catch (err: any) {
      console.log(err)
      toastError(err?.message || 'Verify thất bại.')
    } finally {
      if (verifySocket) {
        verifySocket.disconnect()
      }
      setVerifyingIds((prev) => {
        const next = new Set(prev)
        next.delete(accountId)
        return next
      })
    }
  }, [zaloAccountSessions, crmUserIdNumber])

  const checkSessionsActive = useCallback(async (accounts: any[]) => {
    const ids = accounts.map((a: any) => Number(a?.id)).filter((n) => Number.isFinite(n) && n > 0)
    if (ids.length === 0) return
    setAccountActiveMap((prev) => {
      const next = new Map(prev)
      ids.forEach((id) => next.set(id, null))
      return next
    })
    const results = await Promise.allSettled(
      ids.map(async (id) => {
        const resp: any = await apiCheckSessionActive({ zaloAccountId: id })
        return { id, isActive: !!resp?.data?.isActive }
      })
    )
    setAccountActiveMap((prev) => {
      const next = new Map(prev)
      results.forEach((r, i) => {
        const id = ids[i]
        if (r.status === 'fulfilled') {
          next.set(id, r.value.isActive)
        } else {
          next.set(id, null)
        }
      })
      return next
    })
  }, [])

  const fetchZaloAccounts = useCallback(async () => {
    try {
      setIsLoading(true)
      const { managedAccountIds, ...apiSearch } = search || {}

      const resp = await apiGetListZaloAccounts({
        orderBy: '"createdAt" asc',
        page: currentPage,
        size: pageSize,
        ...apiSearch,
        ...(cpCodesFilter.length > 0 ? { cpCodes: cpCodesFilter } : {}),
      })

      let accounts = toArray(resp?.data?.zaloAccounts)
      if (Array.isArray(managedAccountIds) && managedAccountIds.length > 0) {
        accounts = accounts.filter((a: any) => managedAccountIds.includes(Number(a?.id)))
      }

      if (resp.status === 200) {
        setZaloAccounts(accounts)
        await fetchZaloAccountSessions(accounts)
        if (authState?.account?.canCheckSessionActive && accounts.length > 0) {
          void checkSessionsActive(accounts)
        }
      }
    } catch (error: any) {
      console.log(error)
      toastError('Can not get Zalo accounts list!')
    } finally {
      setIsLoading(false)
    }
  }, [currentPage, pageSize, setIsLoading, fetchZaloAccountSessions, search, cpCodesFilter, authState?.account?.canCheckSessionActive])

  // Finalize QR-login: single server-side atomic call that upserts the zalo account,
  // creates the session, and ensures a primary MANAGE permission for the caller — all
  // inside one DB transaction. Replaces the previous 3-step client sequence which had
  // a TOCTOU race when two CRM users scanned the same Zalo account concurrently.
  const ensureDataAfterLogin = useCallback(async (sessionData: any): Promise<'CREATED' | 'EXISTS' | 'BLOCKED'> => {
    // UID exceeds Number.MAX_SAFE_INTEGER — keep as string end-to-end.
    const uidString = String(sessionData?.uid ?? '').trim()
    if (!uidString || !/^\d+$/.test(uidString)) {
      toastError('Can not find Zalo UID in session information.')
      return 'BLOCKED'
    }
    if (!crmUserIdNumber) {
      toastError('Cannot determine CRM user.')
      return 'BLOCKED'
    }

    const displayName = sessionData?.user?.displayName || String(sessionData?.uid) || 'bạn'
    const cookieArray = Array.isArray(sessionData?.cookie) ? sessionData.cookie : []
    const sessionCode = cookieArray.find((item: any) => item?.key === 'zlogin_session')?.value
      || cookieArray[0]?.value
      || ''

    try {
      const credentials = {
        cookie: Array.isArray(sessionData?.cookie) ? sessionData.cookie : [],
        imei: sessionData?.imei || '',
        userAgent: sessionData?.userAgent || '',
      }
      const resp = await apiCompleteZaloLogin({
        zaloUid: uidString,
        displayName,
        accountType: sessionData?.accountType === 'OA' ? 'OA' : 'PERSONAL',
        phone: sessionData?.user?.phoneNumber || '',
        avatarUrl: sessionData?.user?.avatar || '',
        sessionCode,
        startMethod: 'QR',
        ipAddress: sessionData?.publicIp || '',
        userAgent: sessionData?.userAgent || '',
        deviceInfo: sessionData?.imei || '',
        credentials,
      })

      if (resp?.status !== 200) {
        toastError('Failed to finalize Zalo login!')
        return 'BLOCKED'
      }

      const data = resp?.data || {}
      const account = data?.zaloAccount
      const session = data?.zaloSession
      const accountId = toIntNumber(account?.id)
      if (accountId) {
        setCurrentSessionContext({
          accountId,
          sessionId: session?.id || null,
        })
      }
      await fetchZaloAccounts()
      return data?.isNewAccount ? 'CREATED' : 'EXISTS'
    } catch (err: any) {
      const message = String(err?.message || '')
      // Server-side reject when account is locked by another active session, or when
      // the caller's permission has been revoked.
      if (/revoked|permission/i.test(message)) {
        toastError('Your access to this Zalo account has been revoked.')
        return 'BLOCKED'
      }
      toastError(err?.message || 'Failed to finalize Zalo login!')
      return 'BLOCKED'
    }
  }, [crmUserIdNumber, fetchZaloAccounts])

  const disconnectSocket = useCallback(() => {
    if (!socketRef.current) {
      return
    }

    if (socketRef.current.connected && crmUserId) {
      socketRef.current.emit('cancel-qr', { userId: crmUserId })
    }

    socketRef.current.off('connect')
    socketRef.current.off('connect_error')
    socketRef.current.off('qr-generated')
    socketRef.current.off('qr-scanned')
    socketRef.current.off('qr-expired')
    socketRef.current.off('login-success')
    socketRef.current.off('login-failure')
    socketRef.current.disconnect()
    socketRef.current = null
  }, [crmUserId])

  const connectSocketAndAuthenticateByUserId = useCallback(() => {
    if (!crmUserId || !crmUserIdNumber) {
      setStatus('Không tìm thấy userId CRM. Vui lòng đăng nhập lại CRM.')
      return
    }

    const authToken = localStorage.getItem(AUTH_TOKEN_KEY) || ''
    if (!authToken) {
      setStatus('Phiên CRM đã hết hạn. Vui lòng đăng nhập lại.')
      return
    }

    disconnectSocket()

    const zaloSocketUrl = process.env.REACT_APP_ZALO_SOCKET_URL || 'https://koiosops.com'
    const socket = io(zaloSocketUrl, {
      transports: ['websocket'],
      path: process.env.REACT_APP_ZALO_SOCKET_PATH || '/zalo-login/socket.io',
      auth: { token: authToken },
    })

    socketRef.current = socket

    socket.on('connect_error', (err) => {
      setStatus(`Kết nối server thất bại: ${err.message}`)
    })

    socket.on('connect', () => {
      // Each time reconnecting the socket, it will cancel the old QR (if any) and create a new QR
      // to avoid the case where the old QR is scanned and then the socket connects to the server, it will not receive that QR scanned event
      setStatus('Đang kết nối server...')
      setQr(null)
      socket.emit('cancel-qr', { userId: crmUserId })

      setStatus('Đang tạo mã QR mới...')
      socket.emit('get-qr', { userId: crmUserId })
    })

    socket.on('qr-generated', (qrImageBase64: string) => {
      setQr(qrImageBase64)
      setStatus('Vui lòng quét mã QR để đăng nhập.')
    })

    socket.on('qr-scanned', () => {
      setStatus('Đã quét mã. Vui lòng xác nhận trên điện thoại.')
    })

    socket.on('qr-expired', () => {
      setStatus('Mã QR đã hết hạn.')
    })

    socket.on('login-success', async (data: any) => {
      setQr(null)
      setStatus('Đang xác thực phiên đăng nhập...')
      const sessionToken = data?.sessionToken
      if (!sessionToken) {
        setStatus('Phản hồi đăng nhập không hợp lệ (thiếu sessionToken).')
        toastError('Login response missing sessionToken!')
        return
      }
      try {
        const ack: any = await socket.timeout(10_000).emitWithAck('claim-session', { sessionToken })
        if (!ack?.ok || !ack?.session) {
          const reason = ack?.error || 'unknown'
          setStatus(`Không lấy được phiên đăng nhập (${reason}).`)
          toastError('Failed to claim Zalo session!')
          return
        }
        const session = ack.session
        const displayName = session?.user?.displayName || 'bạn'
        setStatus(`Đăng nhập thành công! Xin chào, ${displayName}.`)
        const result = await ensureDataAfterLogin(session)
        if (result === 'CREATED') {
          toastSuccess('Login success and new session created.')
        }
        if (result === 'EXISTS') {
          toastSuccess('Login success. Session already exists, not creating new one.')
        }
      } catch (error) {
        console.log(error)
        const message = error instanceof Error
          ? error.message
          : 'Đăng nhập thành công nhưng đồng bộ dữ liệu vào hệ thống thất bại.'
        setStatus(message)
        toastError('Login success but failed to sync data!')
      }
    })

    socket.on('login-failure', () => {
      setStatus('Đăng nhập thất bại. Vui lòng thử lại.')
    })
  }, [crmUserId, crmUserIdNumber, disconnectSocket, ensureDataAfterLogin])

  const requestNewQR = useCallback(() => {
    if (!crmUserId) {
      setStatus('Không tìm thấy userId CRM. Vui lòng đăng nhập lại CRM.')
      return
    }

    setStatus('Đang tạo mã QR mới...')
    setQr(null)
    connectSocketAndAuthenticateByUserId()
  }, [connectSocketAndAuthenticateByUserId, crmUserId])

  // Update normally with fields: name, note
  // if there is status update, then need to check more:
  // 1. if the updated status is active, then need to check if there is any active session or not before updating.
  // 2. if the updated status is inactive, then need to close the active session at the same time.
  const handleUpdateAccount = async () => {
    try { 
      setIsLoading(true)

      const accountId = modelUpdateAccount?.data?.id
      if (!accountId) {
        toastError('Account ID is missing!')
        return
      }
      
      const infoResp = await apiUpdateListZaloAccounts({
        zaloAccounts: [
          {
            id: accountId,
            displayName: updateAccount?.displayName,
            note: updateAccount?.note,
          },
        ],
      })

      if (infoResp?.status !== 200) {
        toastError('Failed to update account information!')
        return
      }

      // if not updating status then stop here
      if (updateAccount?.status == null) {
        toastSuccess('Account information updated successfully.')
        setModelUpdateAccount({ isOpen: false, data: null })
        await fetchZaloAccounts()
        return
      }

      const respSession = await apiGetListZaloAccountSessions({
        orderBy: '"createdAt" desc',
        page: 1,
        size: 1,
        zaloAccountIds: [accountId],
        status: 'ACTIVE',
      })
      if (respSession?.status !== 200) {
        toastError('Failed to update account ACTIVE!')
        return
      }

      const activeSessions = toArray(respSession?.data?.zaloAccountSessions).length > 0
      // Handle logic by status
      if (updateAccount.status === 'ACTIVE') {
        if (!activeSessions) {
          toastError('Cannot activate account when there is no ACTIVE session!')
          return
        }

        const accResp = await apiUpdateListZaloAccounts({
          zaloAccounts: [{ id: accountId, status: 'ACTIVE' }]
        })

        if (accResp?.status !== 200) {
          toastError('Update account ACTIVE failed!')
          return
        }
        toastSuccess('Account information updated successfully.')
        setModelUpdateAccount({ isOpen: false, data: null })
        await fetchZaloAccounts()
      }

      if (updateAccount.status === 'INACTIVE') {
        // Close session first
        if (activeSessions) {
          const sessions = toArray(respSession?.data?.zaloAccountSessions)

          if (sessions.length > 0) {
            const sessionResp = await apiUpdateListZaloAccountSessions({
              zaloAccountSessions: sessions.map((s: any) => ({
                id: s.id,
                status: 'CLOSED'
              }))
            })

            if (sessionResp?.status !== 200) {
              toastError('Failed to close session!')
              return
            }
          }
        }

        // Update account status
        const accResp = await apiUpdateListZaloAccounts({
          zaloAccounts: [{ id: accountId, status: 'INACTIVE' }]
        })

        if (accResp?.status !== 200) {
          toastError('Update account INACTIVE failed!')
          return
        }

        toastSuccess('Account information updated successfully.')
        setModelUpdateAccount({ isOpen: false, data: null })
        await fetchZaloAccounts()
      }
    } catch (error) {
      console.log(error)
      toastError('Failed to update account information!')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteAccount = async () => {
    try {
      setIsLoading(true)
      const resp = await apiDeleteListZaloAccounts({
        zaloAccounts: [
          {
            id: modelDeleteAccount?.data?.id
          }
        ]
      })
      if (resp.status !== 200) {
        toastError('Failed to delete account due to active account status!')
        return
      }
      toastSuccess('Account deleted successfully.')
      if (Number(currentSessionContext?.accountId) === Number(modelDeleteAccount?.data?.id)) {
        setCurrentSessionContext({ accountId: null, sessionId: null })
      }
      setModelDeleteAccount({ isOpen: false, data: null })
      await fetchZaloAccounts()
    } catch (error) {
      console.log(error)
      toastError('Failed to delete account due to active account status!')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCloseModalLogin = useCallback(() => {
    setModalLogin(false)
    disconnectSocket()
  }, [disconnectSocket])

  const handleSearch = ({ displayName, zaloUid, phone, status }: any) => {
    if (!displayName && !zaloUid && !phone && !status) return
    const zaloUidList = toIntNumberArray(zaloUid)
    if (zaloUid && zaloUidList.length === 0) {
      toastError('UID Zalo không hợp lệ!')
      return
    }
    if (phone) {
      phone = phone.replace(/^0+/, '+84')
    }
    setSearch({
      displayName,
      zaloUids: zaloUidList.length > 0 ? zaloUidList : undefined,
      phone,
      status,
    })
    setCurrentPage(defaultParams.PAGE)
    setPageSize(defaultParams.LIMIT)
  }

  const handleClearSearch = () => {
    formSearch.resetFields()
    setSearch({})
    setCpCodesFilter([])
    setCurrentPage(defaultParams.PAGE)
    setPageSize(defaultParams.LIMIT)
  }

  // This function will find out the zalo accounts that the current logged in CRM user has active sessions.
  const handlerSearchAccountLoginByUser = async () => {
    try {
      const resp = await apiGetListZaloAccountSessions({
        page: 1,
        size: pageSize,
        userIds: crmUserIdNumber ? [crmUserIdNumber] : undefined,
        status: 'ACTIVE',
      })
      if (resp?.status !== 200) {
        toastError('Failed to fetch account sessions!')
        setSearch({})
        setCurrentPage(defaultParams.PAGE)
        setPageSize(defaultParams.LIMIT)
        return
      }
      const sessions = toArray(resp?.data?.zaloAccountSessions)
      const managedAccountIds = Array.from(
        new Set(
          sessions
            .filter((s: any) => Number(s?.userId) === crmUserIdNumber && isActiveStatus(s?.status))
            .map((s: any) => Number(s?.zaloAccountId))
            .filter((id: number) => Number.isFinite(id) && id > 0)
        )
      )
      if (managedAccountIds.length === 0) {
        toastError("You don't have any active sessions!")
        setSearch({})
        setCurrentPage(defaultParams.PAGE)
        setPageSize(defaultParams.LIMIT)
        return
      }
      setSearch({ managedAccountIds })
      setCurrentPage(defaultParams.PAGE)
      setPageSize(defaultParams.LIMIT)
    } catch (error) {
      toastError('Failed to search for accounts!')
      setSearch({})
      setCurrentPage(defaultParams.PAGE)
      setPageSize(defaultParams.LIMIT)
    }
  }

  useEffect(() => {
    fetchUser()
    fetchZaloAccounts()
  }, [fetchZaloAccounts, fetchUser])

  useEffect(() => {
    return () => {
      disconnectSocket()
    }
  }, [disconnectSocket])

  useEffect(() => {
    if (!authState?.account?.canGetListZaloAccountCompanies) return
    void (async () => {
      try {
        const res: any = await apiGetListCompanies({ limit: defaultParams.MAX_LIMIT })
        const list = Array.isArray(res?.data?.companies) ? res.data.companies : []
        setCompaniesOptions(list)
      } catch {
        setCompaniesOptions([])
      }
    })()
  }, [authState?.account?.canGetListZaloAccountCompanies])

  // Fetch zalo↔partner mapping for the visible accounts so the "Đối tác" column
  // can render chips without an N+1 per-row request.
  useEffect(() => {
    if (!authState?.account?.canGetListZaloAccountCompanies) return
    const ids = zaloAccounts.map((a: any) => Number(a?.id)).filter((n: number) => Number.isFinite(n) && n > 0)
    if (ids.length === 0) {
      setCompanyMap(new Map())
      return
    }
    void (async () => {
      try {
        const res: any = await apiGetListZaloAccountCompanies({ zaloAccountIds: ids, page: 1, size: 500 })
        const list = Array.isArray(res?.data?.zaloAccountCompanies) ? res.data.zaloAccountCompanies : []
        const next = new Map<number, string[]>()
        list.forEach((m: any) => {
          const aid = Number(m?.zaloAccountId)
          const cp = String(m?.cpCode || '')
          if (!aid || !cp) return
          const arr = next.get(aid) || []
          arr.push(cp)
          next.set(aid, arr)
        })
        setCompanyMap(next)
      } catch {
        setCompanyMap(new Map())
      }
    })()
  }, [zaloAccounts, authState?.account?.canGetListZaloAccountCompanies])

  const handleShowDetail = ({data}: any) => {
    const accountId = Number(data?.id)
    const sessionsByAccount = toArray(zaloAccountSessions)
    .filter((session: any) => Number(session?.zaloAccountId) === accountId)
    .sort((a: any, b: any) => {
      const timeA = new Date(a?.created_at || a?.startedAt || 0).getTime()
      const timeB = new Date(b?.created_at || b?.startedAt || 0).getTime()
      return timeB - timeA
    })

    setTabTransaction({
      data: data,
      sessions: sessionsByAccount
    })
  }

  const renderAction = (data: any) => {
    return (
      <Space size={4}>
        <Tooltip title="Chi tiết">
          <Button size="small" icon={<EyeOutlined />} onClick={() => handleShowDetail({ data })} />
        </Tooltip>
        {authState?.account?.canGetListZaloAccountUserPermissions && (
          <Tooltip title="Phân quyền">
            <Button
              size="small"
              icon={<KeyOutlined />}
              onClick={() => navigate(`${ZALO_ACCOUNT}${PERMISSIONS}?accountId=${data?.id}`)}
            />
          </Tooltip>
        )}
        {authState?.account?.canUpdateListZaloAccounts && (
          <Tooltip title="Chỉnh sửa">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                setModelUpdateAccount({ isOpen: true, data })
                setUpdateAccount({
                  displayName: data?.displayName,
                  note: data?.note,
                })
              }}
            />
          </Tooltip>
        )}
        {authState?.account?.canDeleteListZaloAccounts && (
          <Tooltip title="Xoá">
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => setModelDeleteAccount({ isOpen: true, data })}
            />
          </Tooltip>
        )}
      </Space>
    )
  }

  const renderDetailAccount = (account: any) => {
    return (
      <>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
              <h1>{account?.displayName}</h1>
              <p>
                {renderType(account?.status)}
              </p>
              <p>{renderType(account?.accountType)}</p>
            </div>
            <div style={{ display: 'flex', gap: 20, marginBottom: 20 }}>
              <p><b>Mã:</b> {account?.id}</p>
              <p><b>UID:</b> {account?.zaloUid}</p>
              <p><b>SĐT:</b> {formatPhone(account?.phone)}</p>
            </div>
            <div>
              <p><b>Sử dụng gần nhất:</b> {formatDate(account?.lastUsedAt)}</p>
            </div>
          </div>
          <div style={{ flex: 1 }}>
            {/* This place will contain the interaction functions with zalo account sessions or configs in the future */}
          </div>
      </div>
      </>
    )
  }

  const renderDetail = (account: any, sessions: any) => {
    return (
      <>
        <Button onClick={() => setTabTransaction({})}
          icon={<LeftCircleOutlined />}
          style={{ background: 'gray', color: 'white' }}>
          Trở lại
      </Button>
      {renderDetailAccount(account)}
      <Tabs
        size='middle'
        items={[
          {
            key: 'info',
            label: 'Thông tin chung',
            children: 
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
                <div style={{ flex: 1 }}>
                  <p><b>Mã account:</b> {account?.id}</p>
                  <p><b>UID Zalo:</b> {account?.zaloUid}</p>
                  <p><b>Số điện thoại:</b> {formatPhone(account?.phone)}</p>
                  <p><b>Ghi chú:</b> {account?.note}</p>
                  <p><b>Ngày tạo:</b> {formatDate(account?.created_at)}</p>
                </div>
                <div style={{ flex: 1 }}>
                  <p><b>Tên hiển thị:</b> {account?.displayName}</p>
                  <p><b>Loại account:</b> {renderType(account?.accountType)}</p>
                  <p><b>Trạng thái:</b> {renderType(account?.status)}</p>
                  <p><b>Cập nhật lúc:</b> {formatDate(account?.updated_at)}</p>
                </div>
              </div>
          },
          {
            key: 'sessions',
            label: 'Phiên sử dụng',
            children: 
              <div>
                <CustomTable
                  namePage='session'
                  columns={zaloAccountSessionColumns}
                  dataSource={sessions}
                  pageSize={pageSize}
                  setPageSize={setPageSize}
                  total={sessions.length}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                />
              </div>
          },
        ]}
        />
      </>
    )
  }

  const renderLoginAndAccount = () => {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1>Danh sách Zalo Account</h1>

          <div style={{ display: 'flex', gap: 10 }}>
            <Button
              onClick={() => {
                setModalLogin(true)
                setStatus('Đang kết nối tới máy chủ...')
                setQr(null)
                connectSocketAndAuthenticateByUserId()
              }}
              type='primary'
              disabled={!crmUserId}
            >
              <LoginOutlined /> Đăng nhập Zalo
            </Button>
          </div>
        </div>

        <Form form={formSearch} onFinish={handleSearch}>
           <div style={{ display: 'flex', alignItems: 'start', flexWrap: 'wrap', gap: 20 }}>
            <Form.Item name="displayName" label="Tên hiển thị">
              <Input placeholder="Nhập tên hiển thị" />
            </Form.Item>
            <Form.Item name="zaloUid" label="UID Zalo">
              <Input placeholder="Nhập UID Zalo" />
            </Form.Item>
            <Form.Item name="phone" label="Số điện thoại">
              <Input placeholder="Nhập số điện thoại" />
            </Form.Item>
            <Form.Item name="status" label="Trạng thái">
              <Select placeholder="Chọn trạng thái">
                <Select.Option value="ACTIVE">ACTIVE</Select.Option>
                <Select.Option value="INACTIVE">INACTIVE</Select.Option>
              </Select>
            </Form.Item>
            {authState?.account?.canGetListZaloAccountCompanies && (
              <Form.Item label="Đối tác">
                <Select
                  mode="multiple"
                  allowClear
                  maxTagCount={3}
                  placeholder="Lọc theo đối tác"
                  style={{ minWidth: 280 }}
                  value={cpCodesFilter}
                  onChange={(values) => {
                    setCpCodesFilter(values)
                    setCurrentPage(defaultParams.PAGE)
                  }}
                  options={companiesOptions
                    .filter((c) => !!c.code)
                    .map((c) => ({ value: c.code, label: c.name ? `${c.code} — ${c.name}` : c.code }))}
                />
              </Form.Item>
            )}
            <Space style={{ marginBottom: 24 }}>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                Tìm kiếm
              </Button>
              <Button type="primary" style={{ background: '#289e45', color: 'white' }} onClick={handlerSearchAccountLoginByUser} icon={<SearchOutlined />}>
                Tìm kiếm tài khoản do bạn quản lý
              </Button>
              <Button onClick={handleClearSearch} icon={<UndoOutlined />}></Button>
            </Space>
          </div>
        </Form>


        <Modal
          title='Zalo QR Code'
          open={modalLogin}
          onCancel={handleCloseModalLogin}
          footer={null}
        >
          <p>Trạng thái: {status}</p>
          {qr ? (
            <div style={{ textAlign: 'center' }}>
              <img src={qr} alt='Zalo QR Code' style={{ width: '100%', maxWidth: 320 }} />
            </div>
          ) : (
            !status.includes('thành công') && <p>Đang tải...</p>
          )}
          {(status.includes('hết hạn') || status.includes('thất bại')) && (
            <Button type='primary' onClick={requestNewQR}>Tạo lại mã QR</Button>
          )}
        </Modal>

        <CustomTable
          namePage='tài khoản zalo'
          columns={zaloAccountColumns}
          dataSource={zaloAccounts}
          pageSize={pageSize}
          setPageSize={setPageSize}
          total={zaloAccounts.length}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          rowKey='id'
        />
      </div>
    )
  }

  return (
    <>
      {tabTransaction?.data ? renderDetail(tabTransaction.data, tabTransaction.sessions) : renderLoginAndAccount()}
      <Modal
        title='Cập nhật Zalo Account'
        open={modelUpdateAccount?.isOpen}
        onCancel={() => setModelUpdateAccount({ isOpen: false, data: null })}
        footer={null}>
        <Divider />
        <div style={{ marginBottom: 20 }}>
          <div style={{ marginBottom: 10 }}>Tên hiển thị</div>
          <Input
            value={updateAccount?.displayName}
            onChange={e => setUpdateAccount((prev: any) => ({ ...prev, displayName: e.target.value }))}
          />
        </div>
        <div style={{ marginBottom: 20 }}>
          <div style={{ marginBottom: 10 }}>Ghi chú</div>
          <Input
            value={updateAccount?.note}
            onChange={e => setUpdateAccount((prev: any) => ({ ...prev, note: e.target.value }))}
          />
        </div>
        <div style={{ marginBottom: 20 }}>
          <div style={{ marginBottom: 10 }}>Trạng thái</div>
          <Select
            value={updateAccount?.status}
            onChange={value => setUpdateAccount((prev: any) => ({ ...prev, status: value }))}
            style={{ width: '100%' }}
            placeholder='Chọn trạng thái'
            options={[
              { label: 'ACTIVE', value: 'ACTIVE' },
              { label: 'INACTIVE', value: 'INACTIVE' },
            ]}
          />
        </div>
        <div style={{ textAlign: 'right' }}>
          <Button onClick={handleUpdateAccount} type='primary' htmlType='submit'>Cập nhật</Button>
        </div>
      </Modal>
      <Modal
        title='Xóa Zalo Account'
        open={modelDeleteAccount?.isOpen}
        onCancel={() => setModelDeleteAccount({ isOpen: false, data: null })}
        footer={null}>
          <p>Bạn có chắc chắn muốn xóa Zalo Account này không?</p>
          <div style={{ textAlign: 'center' }}>
            <Button onClick={handleDeleteAccount} type='primary' htmlType='submit'>Xóa</Button>
          </div>
      </Modal>

      <FriendsModal
        open={friendsModal.open}
        account={friendsModal.account}
        canSync={!!authState?.account?.canSyncZaloAccountFriends}
        onClose={() => setFriendsModal({ open: false, account: null })}
        onSynced={() => { void fetchZaloAccounts() }}
      />

      <CompanyMappingModal
        open={companyModal.open}
        account={companyModal.account}
        canAdd={!!authState?.account?.canAddListZaloAccountCompanies}
        canDelete={!!authState?.account?.canDeleteListZaloAccountCompanies}
        onClose={() => setCompanyModal({ open: false, account: null })}
        onChanged={() => { void fetchZaloAccounts() }}
      />
    </>
  )
}
